import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {HTMLElement | null} Element focused before the non-native drawer opened. */
  #cartDrawerTrigger = null;
  /** @type {((event: KeyboardEvent) => void) | null} Keydown handler while the drawer is open. */
  #cartDrawerKeydown = null;

  /**
   * Opens the non-native cart drawer with focus management (focus in, Escape, focus trap).
   * @param {HTMLElement} dialog
   */
  #openCartDrawer(dialog) {
    this.#cartDrawerTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.classList.add('active');
    dialog.setAttribute('open', '');

    this.#cartDrawerKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.#closeCartDrawer(dialog);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.#cartDrawerKeydown);

    // Move focus into the drawer. Focus the dialog container itself (it has
    // tabindex="-1" and persists even when the drawer re-renders its contents on
    // open, which would otherwise clobber focus placed on an inner element).
    setTimeout(() => {
      if (dialog.hasAttribute('open')) dialog.focus();
    }, 100);
  }

  /**
   * Closes the non-native cart drawer, tears down listeners, and restores focus.
   * @param {HTMLElement} dialog
   */
  #closeCartDrawer(dialog) {
    dialog.classList.remove('active');
    dialog.removeAttribute('open');
    if (this.#cartDrawerKeydown) {
      document.removeEventListener('keydown', this.#cartDrawerKeydown);
      this.#cartDrawerKeydown = null;
    }
    if (this.#cartDrawerTrigger) {
      this.#cartDrawerTrigger.focus();
      this.#cartDrawerTrigger = null;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if(typeof dialog.showModal === 'function') dialog.showModal();
    if(dialog.classList.contains('cart-drawer__dialog')){
      // Non-native drawer (styled <div>, not <dialog>): manage focus + keyboard here,
      // since the native showModal() path below is skipped for this element.
      if(dialog.hasAttribute('open')) {
        this.#closeCartDrawer(dialog);
      } else {
        this.#openCartDrawer(dialog);
      }
      return;
    }
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    
    if(dialog.classList.contains('cart-drawer__dialog')){
      this.#closeCartDrawer(dialog);
      return;
    } else {
      if (!dialog.open) return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if(typeof dialog.close === 'function') dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      if(event.target instanceof Element && event.target.classList.contains('facets-remove__pill')){
        return;
      }
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
